// Chess Bounty — browser entry.
// Dynamic (headless) connects the purse wallet; Unlink shields the deposit,
// wager, and payout; the server is the authoritative chess referee.
import {
  createDynamicClient, waitForClientInitialized, getAvailableWalletProvidersData,
  connectAndVerifyWithWalletProvider, getActiveNetworkData, switchActiveNetwork,
  addNetwork, NetworkNotAddedError, WalletAccountAlreadyVerifiedError,
  getWalletAccounts, getPrimaryWalletAccount, logout as dlogout,
} from '@dynamic-labs-sdk/client';
import { addEvmExtension } from '@dynamic-labs-sdk/evm';
import { createWalletClientForWalletAccount } from '@dynamic-labs-sdk/evm/viem';
import { account as uAccount, evm as uEvm, createUnlinkClient } from '@unlink-xyz/sdk/browser';
import { buildDeriveSeedMessage } from '@unlink-xyz/sdk/crypto';
import { parseUnits, formatUnits, createPublicClient, http, defineChain } from 'viem';

// Build our own viem PublicClient against the known Arc RPC. Dynamic's
// networkData for Arc carries no RPC URL (Arc isn't a Dynamic-native chain),
// so deriving a public client from it throws "No URL provided to the Transport".
function arcPublicClient(cfg) {
  const chain = defineChain({
    id: cfg.chainId, name: 'Arc Testnet',
    nativeCurrency: { name: 'USDC', symbol: 'USDC', decimals: 18 },
    rpcUrls: { default: { http: [cfg.publicRpcUrl] } },
  });
  return createPublicClient({ chain, transport: http(cfg.publicRpcUrl) });
}

const $ = (id) => document.getElementById(id);
const state = {
  cfg: null, dynamic: null, walletAccount: null, eoa: null,
  walletClient: null, publicClient: null, networkData: null,
  unlink: null, unlinkAddr: null, game: null, sel: null, busy: false,
  drag: null, shielded: 0, maxWager: 0,
};

// ---------- console / logging ----------
function log(msg, cls = '') {
  const c = $('console');
  const line = document.createElement('div');
  if (cls) line.className = cls;
  line.textContent = msg;
  c.appendChild(line);
  c.scrollTop = c.scrollHeight;
}
const short = (a) => (a ? a.slice(0, 10) + '…' + a.slice(-6) : '—');
function setStatus(html, cls = '') { const s = $('status'); s.className = 'status' + (cls ? ' ' + cls : ''); s.innerHTML = html; }

// Log a tx line, linking the on-chain hash to the explorer when available.
function logTxn(prefix, ...objs) {
  const hash = objs.map((o) => o && (o.txHash || o.hash)).find(Boolean);
  const id = objs.map((o) => o && o.txId).find(Boolean);
  const c = $('console');
  const line = document.createElement('div'); line.className = 'info';
  line.append(prefix + ' ');
  if (hash) {
    const a = document.createElement('a');
    a.href = state.cfg.explorer + '/tx/' + hash; a.target = '_blank'; a.rel = 'noopener';
    a.textContent = hash.slice(0, 12) + '…' + hash.slice(-8);
    line.append(a);
  } else if (id) line.append(id);
  c.appendChild(line); c.scrollTop = c.scrollHeight;
}

// Make sure the connected wallet is on Arc before signing; nudge it to switch.
async function ensureOnArc() {
  try {
    const { networkData } = await getActiveNetworkData({ walletAccount: state.walletAccount });
    if (String(networkData?.chainId) === String(state.cfg.chainId)) return;
  } catch {}
  log('switch your wallet to Arc Testnet…', 'warn');
  await ensureArcNetwork();
  try { state.walletClient = await createWalletClientForWalletAccount({ walletAccount: state.walletAccount }); } catch {}
}

// ---------- USDC helpers ----------
const U = () => state.cfg.usdcDecimals;
const toUnits = (v) => parseUnits(String(v), U()).toString();
const fromUnits = (v) => formatUnits(BigInt(v), U());

// ---------- ERC-20 balance ----------
const ERC20_BAL = [{ type: 'function', name: 'balanceOf', stateMutability: 'view', inputs: [{ name: 'a', type: 'address' }], outputs: [{ type: 'uint256' }] }];
async function publicUsdc() {
  try {
    const raw = await state.publicClient.readContract({ address: state.cfg.usdc, abi: ERC20_BAL, functionName: 'balanceOf', args: [state.eoa] });
    return fromUnits(raw);
  } catch (e) { return 'err'; }
}
async function shieldedUsdc() {
  try {
    const { balances } = await state.unlink.getBalances({ token: state.cfg.usdc });
    const b = (balances || []).find((x) => (x.token || '').toLowerCase() === state.cfg.usdc) || balances?.[0];
    return b ? fromUnits(b.amount ?? b.balance ?? '0') : '0';
  } catch { return '0'; }
}

// ---------- boot ----------
function gateThreshold() { return 2 * state.cfg.minStakeUsdc; } // unlock board at 2x min wager

// Grey unicode chess-piece rain backdrop. Kept low-contrast so the white UI pops.
function initRain() {
  const canvas = document.getElementById('rain');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const CHARS = '♔♕♖♗♘♙♚♛♜♝♞♟'.split('');
  const font = 20;        // glyph size
  const colW = font + 6;  // column spacing (pieces are wider than code chars)
  let cols, drops, dpr = Math.max(1, window.devicePixelRatio || 1);
  function resize() {
    canvas.width = Math.floor(innerWidth * dpr);
    canvas.height = Math.floor(innerHeight * dpr);
    canvas.style.width = innerWidth + 'px';
    canvas.style.height = innerHeight + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    cols = Math.ceil(innerWidth / colW);
    drops = Array.from({ length: cols }, () => Math.floor(Math.random() * -60));
    ctx.font = font + 'px ui-monospace, monospace';
    ctx.textBaseline = 'top';
  }
  resize();
  addEventListener('resize', resize);
  let last = 0;
  const step = 90; // ms per row advance — slow, calm rain
  function frame(t) {
    requestAnimationFrame(frame);
    if (t - last < step) return;
    last = t;
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    ctx.fillRect(0, 0, innerWidth, innerHeight);
    for (let i = 0; i < cols; i++) {
      const ch = CHARS[(Math.random() * CHARS.length) | 0];
      const y = drops[i] * colW;
      ctx.fillStyle = Math.random() > 0.92 ? 'rgba(165,165,165,0.5)' : 'rgba(85,85,85,0.4)';
      ctx.fillText(ch, i * colW, y);
      if (y > innerHeight && Math.random() > 0.975) drops[i] = Math.floor(Math.random() * -20);
      drops[i]++;
    }
  }
  requestAnimationFrame(frame);
}

async function boot() {
  initRain();
  state.cfg = await (await fetch('/api/config')).json();
  $('hr').textContent = '─'.repeat(400);
  $('wager').min = state.cfg.minStakeUsdc;
  $('wager').max = state.cfg.maxStakeUsdc;
  $('wager').value = state.cfg.minStakeUsdc;
  $('gateAmt').textContent = gateThreshold();
  $('gateNeed').textContent = gateThreshold();
  $('wagerLabel').textContent = `wager (${state.cfg.minStakeUsdc}–${state.cfg.maxStakeUsdc})`;
  refreshHouse();
  buildBoardChrome();
  renderEmptyBoard();
  $('connectBtn').onclick = () => action(doConnect);
  $('logoutBtn').onclick = () => action(logout);
  $('refreshBtn').onclick = () => action(refreshBalances);
  $('depBtn').onclick = () => action(() => deposit($('depAmt').value));
  $('fundBtn').onclick = () => action(() => deposit($('fundAmt').value));
  $('wdBtn').onclick = () => action(withdraw);
  $('playBtn').onclick = () => action(startGame);
  $('resignBtn').onclick = () => action(resign);
  updateStage();
  await restoreSession();
  log('ready — connect your purse wallet to begin', 'info');
}

// connect | fund | play — the board stays hidden until the player has shielded
// at least 2x the minimum wager (or has a live game to resume).
function updateStage() {
  const liveGame = state.game && (state.game.status === 'active' || state.game.status === 'awaiting_wager');
  const connected = state.unlink || state.unlinkAddr; // derived client OR remembered session
  let stage = 'connect';
  if (connected) stage = (Number(state.shielded || 0) >= gateThreshold() || liveGame) ? 'play' : 'fund';
  for (const s of ['Connect', 'Fund', 'Play']) $('stage' + s).classList.toggle('show', stage.toLowerCase() === s.toLowerCase());
  if (stage === 'fund') {
    const now = Number(state.shielded || 0), need = gateThreshold();
    const n = Math.max(0, Math.min(10, Math.round((now / need) * 10)));
    $('gateNow').textContent = now;
    $('gateBar').textContent = '[' + '#'.repeat(n) + '-'.repeat(10 - n) + ']';
  }
}

async function refreshHouse() {
  try {
    const h = await (await fetch('/api/house')).json();
    $('houseStatus').textContent = h.funded ? 'accepting wagers' : 'not funded';
    if (typeof h.maxWager === 'number') {
      state.maxWager = h.maxWager;
      $('wager').max = h.maxWager;
      $('houseMax').textContent = h.maxWager + ' USDC';
      $('wagerLabel').textContent = `wager (${state.cfg.minStakeUsdc}–${h.maxWager})`;
    }
  } catch {}
}

// ---------- Dynamic connect ----------
async function ensureDynamic() {
  if (state.dynamic) return state.dynamic;
  state.dynamic = createDynamicClient({
    environmentId: state.cfg.dynamicEnvId,
    metadata: { name: 'Chess Bounty', url: location.origin },
  });
  addEvmExtension();
  await waitForClientInitialized();
  return state.dynamic;
}

async function doConnect() {
  log('initializing Dynamic…', 'info');
  await ensureDynamic();
  const providers = (getAvailableWalletProvidersData() || []).filter((p) => (p.chain || '').toUpperCase() === 'EVM' || /evm$/.test(p.key || ''));
  if (!providers.length) throw new Error('no EVM wallet detected — install MetaMask or similar');
  if (providers.length === 1) return connectWith(providers[0].key);
  const box = $('walletPicker'); box.innerHTML = '';
  providers.forEach((p) => {
    const b = document.createElement('button');
    b.style.width = '100%'; b.style.marginTop = '6px';
    b.textContent = 'connect ' + (p.metadata?.displayName || p.key);
    b.onclick = () => action(() => connectWith(p.key));
    box.appendChild(b);
  });
  log('multiple wallets found — choose one', 'info');
  throw new Error('choose a wallet above');
}

// Lazily derive the Unlink client only when the user signs something. A
// remembered session shows balances/board without it.
async function ensureUnlinkClient() {
  if (state.unlink) return;
  await doConnect();
  if (!state.unlink) throw new Error('connect your wallet to continue');
}

async function connectWith(key) {
  $('walletPicker').innerHTML = '';
  log('connecting ' + key + '…', 'info');
  let wa;
  try {
    wa = await connectAndVerifyWithWalletProvider({ walletProviderKey: key });
  } catch (e) {
    const already = e instanceof WalletAccountAlreadyVerifiedError || /already verified/i.test(e?.message || '');
    if (!already) throw e;
    log('wallet already connected — reusing session', 'info');
    const accts = (await getWalletAccounts()) || [];
    wa = accts.find((a) => a && (a.address || a.accountAddress)) || (await getPrimaryWalletAccount?.());
    if (!wa) throw e;
  }
  state.walletAccount = wa;
  state.eoa = wa.address || wa.accountAddress;
  log('connected ' + short(state.eoa), 'ok');
  await ensureArcNetwork();
  state.walletClient = await createWalletClientForWalletAccount({ walletAccount: wa });
  state.publicClient = arcPublicClient(state.cfg);
  try { state.networkData = (await getActiveNetworkData({ walletAccount: wa })).networkData; } catch {}
  await deriveUnlink();
  showConnected();
  postJson('/api/session', { eoa: state.eoa, unlinkAddr: state.unlinkAddr }).catch(() => {}); // remember
  await refreshBalances();
  await resumeActiveGame();
}

function showConnected() {
  $('walletDisconnected').style.display = 'none';
  $('walletConnected').style.display = 'block';
  $('eoa').textContent = state.eoa;
  $('unlinkAddr').textContent = state.unlinkAddr;
  $('netpill').textContent = 'arc'; $('netpill').className = 'pill on';
  state.remembered = false;
}

// Restore a remembered wallet (server cookie) without deriving keys: show the
// address + shielded balance and resume any live game. Signing is deferred.
async function restoreSession() {
  try {
    const s = await (await fetch('/api/session')).json();
    if (!s.connected) return;
    state.eoa = s.eoa; state.unlinkAddr = s.unlinkAddr; state.remembered = true;
    state.shielded = Number(s.shieldedUsdc || 0);
    $('walletDisconnected').style.display = 'none';
    $('walletConnected').style.display = 'block';
    $('eoa').textContent = s.eoa;
    $('unlinkAddr').textContent = s.unlinkAddr;
    $('pubUsdc').textContent = '— sign to read';
    $('shUsdc').textContent = s.shieldedUsdc + ' USDC';
    $('netpill').textContent = 'remembered'; $('netpill').className = 'pill on';
    updateStage();
    log('remembered ' + short(s.eoa) + ' — sign to deposit, withdraw, or wager', 'info');
    await resumeActiveGame();
  } catch {}
}

async function logout() {
  try { await dlogout(); } catch {}
  await postJson('/api/session/logout', {});
  Object.assign(state, { unlink: null, unlinkAddr: null, eoa: null, walletAccount: null, walletClient: null, remembered: false, shielded: 0, game: null, sel: null });
  $('walletConnected').style.display = 'none';
  $('walletDisconnected').style.display = 'block';
  $('netpill').textContent = 'offline'; $('netpill').className = 'pill';
  renderEmptyBoard();
  updateStage();
  log('disconnected', 'info');
}

async function ensureArcNetwork() {
  const cid = state.cfg.chainId;
  const arc = {
    chainId: cid, chainName: 'Arc Testnet',
    nativeCurrency: { name: 'USDC', symbol: 'USDC', decimals: 18 },
    rpcUrls: [state.cfg.publicRpcUrl], blockExplorerUrls: [state.cfg.explorer],
  };
  try {
    await switchActiveNetwork({ walletAccount: state.walletAccount, networkId: String(cid) });
  } catch (e) {
    if (e instanceof NetworkNotAddedError) {
      log('adding Arc Testnet to wallet…', 'info');
      await addNetwork({ walletAccount: state.walletAccount, networkData: e.networkData || arc });
      await switchActiveNetwork({ walletAccount: state.walletAccount, networkId: String(cid) });
    } else {
      log('network switch note: ' + (e?.message || e), 'warn');
    }
  }
}

async function deriveUnlink() {
  const { appId, chainId, unlinkEnv } = state.cfg;
  const message = buildDeriveSeedMessage({ appId, chainId });
  log('sign in your wallet to derive your private Unlink account…', 'info');
  const signature = await state.walletClient.signMessage({ account: state.eoa, message });
  const acct = uAccount.fromEthereumSignature({ signature, appId, chainId });
  state.unlinkAddr = await acct.getAddress();
  state.unlink = createUnlinkClient({
    environment: unlinkEnv, account: acct,
    evm: uEvm.fromViem({ walletClient: state.walletClient, publicClient: state.publicClient }),
  });
  log('registering Unlink account…', 'info');
  await state.unlink.ensureRegistered();
  $('unlinkAddr').textContent = state.unlinkAddr;
  log('Unlink account ready ' + short(state.unlinkAddr), 'ok');
}

async function refreshBalances() {
  if (!state.unlink) return;
  $('pubUsdc').textContent = '…'; $('shUsdc').textContent = '…';
  const [pub, sh] = await Promise.all([publicUsdc(), shieldedUsdc()]);
  state.shielded = Number(sh);
  $('pubUsdc').textContent = pub + ' USDC';
  $('shUsdc').textContent = sh + ' USDC';
  await refreshHouse();
  updateStage();
}

// ---------- deposit / withdraw ----------
async function deposit(rawAmt) {
  const amt = Number(rawAmt);
  if (!(amt > 0)) return log('enter a deposit amount', 'err');
  await ensureUnlinkClient();
  await ensureOnArc();
  log('depositing ' + amt + ' USDC (Permit2 approval + shield)…', 'info');
  const tx = await state.unlink.depositWithApproval({ token: state.cfg.usdc, amount: toUnits(amt) });
  logTxn('deposit submitted', tx);
  log('waiting for deposit to settle…', 'info');
  const c = await tx.wait();
  logTxn('deposit ' + (c.status || 'settled'), c, tx);
  await refreshBalances();
}
async function withdraw() {
  const amt = Number($('wdAmt').value);
  if (!(amt > 0)) return log('enter a withdraw amount', 'err');
  await ensureUnlinkClient();
  await ensureOnArc();
  log('withdrawing ' + amt + ' USDC to ' + short(state.eoa) + '…', 'info');
  const tx = await state.unlink.withdraw({ recipientEvmAddress: state.eoa, token: state.cfg.usdc, amount: toUnits(amt) });
  logTxn('withdraw submitted', tx);
  log('waiting for withdrawal to settle…', 'info');
  const c = await tx.wait();
  logTxn('withdraw ' + (c.status || 'settled'), c, tx);
  await refreshBalances();
}

// ---------- game ----------
async function startGame() {
  if (!state.unlink && !state.unlinkAddr) return log('connect your wallet first', 'err');
  const wager = Number($('wager').value);
  const level = $('level').value;
  if (!(wager >= state.cfg.minStakeUsdc)) return log('minimum wager is ' + state.cfg.minStakeUsdc + ' USDC', 'err');
  const curMax = state.maxWager || state.cfg.maxStakeUsdc;
  if (wager > curMax) return log('max wager right now is ' + curMax + ' USDC (scales with house float)', 'err');
  await ensureUnlinkClient();
  const sh = Number(await shieldedUsdc());
  if (sh < wager) return log(`shielded balance ${sh} < wager ${wager} — deposit first`, 'err');

  setStatus('Starting match…');
  log('starting ' + level + ' match, wager ' + wager + ' USDC', 'info');
  const r = await postJson('/api/game/start', { level, wager, playerUnlink: state.unlinkAddr });
  if (r.error) return log('start failed: ' + r.error, 'err');
  state.game = r;
  await ensureWagerThenActivate(r);
}

// Move the wager to the house (idempotent) and flip the game to active. Used by
// both a fresh start and a resume — the server confirms by watching its own
// shielded balance rise, so re-running is safe.
async function ensureWagerThenActivate(view) {
  // Already paid? (resume, or a prior attempt.) Quick non-blocking check first
  // so we never wait on a poll before the money has even moved.
  let conf = await postJson('/api/game/confirm-wager', { gameId: view.id, quick: true });
  if (conf.status === 'active') return onGameActive(conf);

  if (!state.unlink) { setStatus('Reconnect your wallet to send the wager.', 'lose'); return; }
  await ensureOnArc();
  log('transferring ' + view.wager + ' USDC wager to the house (private)…', 'info');
  setStatus('Transferring your shielded wager to the house…');
  const tx = await state.unlink.transfer({ recipientAddress: view.houseUnlinkAddress, token: state.cfg.usdc, amount: view.wagerUnits });
  logTxn('wager submitted', tx);
  setStatus('Waiting for the wager to settle…');
  const c = await tx.wait();
  state.wagerTxHash = c.txHash || tx.txHash || null;
  logTxn('wager staked', c, tx);
  if (c.status === 'failed') { setStatus('Wager transfer failed.', 'lose'); return; }

  setStatus('Confirming the house received your wager…');
  conf = await postJson('/api/game/confirm-wager', { gameId: view.id });
  if (conf.status === 'active') return onGameActive(conf);
  log('could not confirm wager: ' + (conf.error || 'not detected'), 'err');
  setStatus('Could not confirm the wager yet — your game is saved; refresh to resume.', 'lose');
}

async function onGameActive(view) {
  state.game = view; state.sel = null;
  updateStage();
  render(view);
  $('resignBtn').disabled = false; $('playBtn').disabled = true;
  log('wager confirmed — game on. You are White.', 'ok');
  await refreshBalances();
}

// On (re)connect, pull any unfinished game and resume it so a closed window
// never strands a wager that was already moved to the house.
async function resumeActiveGame() {
  try {
    const r = await (await fetch('/api/game/active?playerUnlink=' + encodeURIComponent(state.unlinkAddr))).json();
    if (!r || r.none) return;
    state.game = r; updateStage();
    if (r.status === 'active') {
      render(r);
      $('resignBtn').disabled = false; $('playBtn').disabled = true;
      setStatus('Resumed your match. Your move (White).');
      log('resumed in-progress match', 'ok');
    } else if (r.status === 'awaiting_wager') {
      log('found a started match — settling the wager…', 'info');
      await ensureWagerThenActivate(r);
    }
  } catch (e) { log('resume check failed: ' + (e?.message || e), 'warn'); }
}

async function sendMove(from, to) {
  setStatus('Thinking…');
  const r = await postJson('/api/game/move', { gameId: state.game.id, from, to });
  if (r.error) { log('move rejected: ' + r.error, 'err'); render(state.game); return; }
  state.game = r;
  render(r);
  for (const e of r.events || []) log(e, /check|pinned/.test(e) ? 'warn' : 'ok');
  if (r.settlement) finishGame(r);
}

async function resign() {
  if (!state.game || state.game.status !== 'active') return;
  const r = await postJson('/api/game/resign', { gameId: state.game.id });
  state.game = r; render(r); finishGame(r);
}

function finishGame(view) {
  const s = view.settlement || {};
  $('resignBtn').disabled = true; $('playBtn').disabled = false;
  if (view.status === 'won') {
    setStatus('checkmate — you win. 2x paid privately.', 'win');
    if (s.error) log('payout error: ' + s.error, 'err');
    else logTxn('won — 2x payout settled', s); // links s.txHash → arcscan
  } else if (view.status === 'lost') {
    setStatus('checkmate — you lose. the house keeps your wager.', 'lose');
    log('lost — the house claimed your staked wager', 'warn');
    if (state.wagerTxHash) logTxn('wager claimed by house', { txHash: state.wagerTxHash });
  } else if (view.status === 'draw') {
    setStatus('draw — wager refunded privately.', 'draw');
    if (s.error) log('refund error: ' + s.error, 'err');
    else logTxn('draw — refund settled', s);
  }
  state.wagerTxHash = null;
  setTimeout(refreshBalances, 1500);
}

// ---------- rendering + interaction (click OR drag-and-drop) ----------
function buildBoardChrome() {
  const ranks = $('ranks'); ranks.innerHTML = '';
  for (let r = 8; r >= 1; r--) { const d = document.createElement('div'); d.textContent = r; ranks.appendChild(d); }
  const files = $('files'); files.innerHTML = '';
  for (const f of 'abcdefgh') { const s = document.createElement('span'); s.textContent = f; files.appendChild(s); }
  $('board').addEventListener('pointerdown', onBoardPointerDown);
}
function renderEmptyBoard() { render({ board: new Array(64).fill(null), legal: [], status: 'idle', lastMove: null }); }

const legalNow = () => (state.game && state.game.status === 'active' ? state.game.legal || [] : []);
const isFrom = (idx) => legalNow().some((m) => m.from === idx);
const isLegalMove = (from, to) => legalNow().some((m) => m.from === from && m.to === to);
const playable = () => state.game && state.game.status === 'active' && !state.busy;

// Full repaint — call when the position changes (move, new game, resume).
function render(view) {
  const board = $('board'); board.innerHTML = '';
  const last = view.lastMove;
  const froms = new Set((view.legal || []).map((m) => m.from));
  for (let dr = 0; dr < 8; dr++) {
    const r = 7 - dr;
    for (let c = 0; c < 8; c++) {
      const idx = r * 8 + c;
      const sq = document.createElement('div');
      sq.className = 'sq ' + ((r + c) % 2 === 1 ? 'light' : 'dark');
      sq.dataset.idx = idx;
      const cell = view.board ? view.board[idx] : null;
      if (cell) {
        const span = document.createElement('span');
        span.className = 'pc ' + (cell.code > 0 ? 'w' : 'b');
        span.textContent = cell.glyph;
        sq.appendChild(span);
      }
      if (last && (last.from === idx || last.to === idx)) sq.classList.add('last');
      if (view.status === 'active' && froms.has(idx)) sq.classList.add('canmove');
      board.appendChild(sq);
    }
  }
  paintHighlights();
  renderCaps(view);
  if (view.status === 'active') {
    const chk = view.check ? '  [check]' : '';
    setStatus('white to move, wager ' + state.game.wager + ' USDC' + chk);
  }
  if (view.moves) $('movelog').textContent = formatMoves(view.moves) || '—';
}

function renderCaps(view) {
  const cap = view.captured || { player: [], ai: [] };
  const tray = (arr) => (arr && arr.length
    ? arr.map((p) => `<span class="pc ${p.code > 0 ? 'w' : 'b'}">${p.glyph}</span>`).join('')
    : '<span class="none">—</span>');
  $('capPlayer').innerHTML = tray(cap.player); // black pieces you took
  $('capAI').innerHTML = tray(cap.ai);         // white pieces the cpu took
}

// Lightweight: toggle selection/target classes on existing squares (keeps piece
// element references valid mid-drag).
function paintHighlights() {
  const board = $('board');
  const legal = legalNow();
  const targets = state.sel != null ? legal.filter((m) => m.from === state.sel).map((m) => m.to) : [];
  for (const sq of board.children) {
    const idx = +sq.dataset.idx;
    sq.classList.toggle('sel', state.sel === idx);
    const t = targets.includes(idx);
    sq.classList.toggle('tgt', t);
    sq.classList.toggle('cap', t && !!sq.querySelector('.pc'));
  }
}

function doMove(from, to) { state.sel = null; action(() => sendMove(from, to)); }

function onBoardPointerDown(ev) {
  if (!playable()) return;
  const sq = ev.target.closest('.sq'); if (!sq) return;
  const idx = +sq.dataset.idx;
  // click-to-move: a piece is selected and this is a legal destination
  if (state.sel != null && isLegalMove(state.sel, idx)) { doMove(state.sel, idx); return; }
  if (isFrom(idx)) { state.sel = idx; paintHighlights(); beginDrag(idx, sq, ev); }
  else { state.sel = null; paintHighlights(); }
}

function beginDrag(from, sq, ev) {
  const span = sq.querySelector('.pc'); if (!span) return;
  const ghost = document.createElement('div');
  ghost.className = 'ghost ' + (span.classList.contains('w') ? 'w' : 'b');
  ghost.textContent = span.textContent;
  ghost.style.left = ev.clientX + 'px'; ghost.style.top = ev.clientY + 'px';
  document.body.appendChild(ghost);
  span.classList.add('drag');
  state.drag = { from, span, ghost, moved: false, sx: ev.clientX, sy: ev.clientY, hover: null };
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerUp, { once: true });
  ev.preventDefault();
}

function onPointerMove(ev) {
  const d = state.drag; if (!d) return;
  d.ghost.style.left = ev.clientX + 'px'; d.ghost.style.top = ev.clientY + 'px';
  if (Math.abs(ev.clientX - d.sx) > 4 || Math.abs(ev.clientY - d.sy) > 4) d.moved = true;
  const sq = document.elementFromPoint(ev.clientX, ev.clientY)?.closest?.('.sq');
  if (d.hover && d.hover !== sq) d.hover.classList.remove('hover');
  if (sq && +sq.dataset.idx !== d.from && isLegalMove(d.from, +sq.dataset.idx)) { sq.classList.add('hover'); d.hover = sq; }
  else d.hover = null;
}

function onPointerUp(ev) {
  const d = state.drag; if (!d) return;
  document.removeEventListener('pointermove', onPointerMove);
  d.ghost.remove();
  d.span.classList.remove('drag');
  if (d.hover) d.hover.classList.remove('hover');
  state.drag = null;
  const sq = document.elementFromPoint(ev.clientX, ev.clientY)?.closest?.('.sq');
  const to = sq ? +sq.dataset.idx : -1;
  if (d.moved) {
    if (to >= 0 && isLegalMove(d.from, to)) doMove(d.from, to);   // dropped on a legal square
    else { state.sel = null; paintHighlights(); }                 // snap back
  } else {
    paintHighlights(); // a tap: keep the piece selected, show its targets (click-to-move)
  }
}

function formatMoves(sans) {
  let out = '';
  for (let i = 0; i < sans.length; i += 2) {
    out += (i / 2 + 1) + '. ' + sans[i] + (sans[i + 1] ? ' ' + sans[i + 1] : '') + '\n';
  }
  return out.trim();
}

// ---------- plumbing ----------
async function postJson(url, body) {
  const r = await fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  try { return await r.json(); } catch { return { error: 'bad response ' + r.status }; }
}
function setBusy(v) {
  state.busy = v;
  for (const id of ['connectBtn', 'depBtn', 'wdBtn', 'playBtn', 'resignBtn', 'refreshBtn']) { const el = $(id); if (el) el.disabled = v; }
  if (!v) { $('resignBtn').disabled = !(state.game && state.game.status === 'active'); $('playBtn').disabled = !!(state.game && state.game.status === 'active'); }
}
async function action(fn) {
  if (state.busy) return;
  setBusy(true);
  try { await fn(); } catch (e) { log('error: ' + (e?.shortMessage || e?.message || e), 'err'); setStatus('Error: ' + (e?.shortMessage || e?.message || e), 'lose'); }
  finally { setBusy(false); }
}

boot().catch((e) => log('boot failed: ' + (e?.message || e), 'err'));
